import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UserPlus, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { AllergiesField } from "@/components/AllergiesField";

const empty = {
  patient_number: "",
  email: "",
  name: "",
  mobile: "",
  age: "",
  gender: "",
  height_cm: "",
  weight_kg: "",
  medical_conditions: "",
  allergies: "",
  food_preferences: "",
  notes: "",
};

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get("new") === "1");
  const [form, setForm] = useState(empty);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/patients")
      .then(({ data }) => setPatients(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...form };
      ["age", "height_cm", "weight_kg"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else payload[k] = Number(payload[k]);
      });
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/patients", payload);
      toast.success(`Patient ${form.patient_number} created. Share the patient number — they'll log in with it.`);
      setForm(empty);
      setOpen(false);
      params.delete("new");
      setParams(params, { replace: true });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const closeDialog = (v) => {
    setOpen(v);
    if (!v) {
      setForm(empty);
      params.delete("new");
      setParams(params, { replace: true });
    }
  };

  return (
    <div className="space-y-6" data-testid="patients-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Patients</p>
          <h1 className="text-3xl font-bold mt-1">All patients</h1>
          <p className="text-muted-foreground mt-1">Create a patient with a unique patient number — they sign in via OTP.</p>
        </div>
        <Dialog open={open} onOpenChange={closeDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-patient-button" className="gap-2 rounded-full">
              <UserPlus size={16} /> Add patient
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-patient-dialog">
            <DialogHeader>
              <DialogTitle>Add a new patient</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2 col-span-2">
                  <Label>Patient number*</Label>
                  <Input required value={form.patient_number} onChange={(e) => set("patient_number", e.target.value)} placeholder="e.g. 0000608000" data-testid="new-patient-number" />
                </div>
                <div className="space-y-2">
                  <Label>Full name*</Label>
                  <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="new-patient-name" />
                </div>
                <div className="space-y-2">
                  <Label>Email*</Label>
                  <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="new-patient-email" />
                </div>
                <div className="space-y-2">
                  <Label>Mobile</Label>
                  <Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="new-patient-mobile" />
                </div>
                <div className="space-y-2">
                  <Label>Age</Label>
                  <Input type="number" value={form.age} onChange={(e) => set("age", e.target.value)} data-testid="new-patient-age" />
                </div>
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <select
                    value={form.gender}
                    onChange={(e) => set("gender", e.target.value)}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="new-patient-gender"
                  >
                    <option value="">— Select —</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Height (cm)</Label>
                  <Input type="number" value={form.height_cm} onChange={(e) => set("height_cm", e.target.value)} data-testid="new-patient-height" />
                </div>
                <div className="space-y-2">
                  <Label>Weight (kg)</Label>
                  <Input type="number" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} data-testid="new-patient-weight" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Medical conditions</Label>
                <Textarea rows={2} value={form.medical_conditions} onChange={(e) => set("medical_conditions", e.target.value)} data-testid="new-patient-medical" />
              </div>
              <div className="space-y-2">
                <Label>Allergies</Label>
                <AllergiesField value={form.allergies} onChange={(v) => set("allergies", v)} testIdPrefix="new-patient-allergies" />
              </div>
              <div className="space-y-2">
                <Label>Food preferences</Label>
                <Textarea rows={2} value={form.food_preferences} onChange={(e) => set("food_preferences", e.target.value)} data-testid="new-patient-prefs" />
              </div>
              <Button type="submit" disabled={creating} className="w-full" data-testid="create-patient-button">
                {creating ? "Creating…" : "Create patient"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="nt-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-muted-foreground">Loading…</div>
        ) : patients.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="no-patients">
            <UserPlus size={32} className="mx-auto mb-3 text-primary" />
            <p className="font-semibold">No patients yet</p>
            <p className="text-sm mt-1">Add your first patient to get started.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {patients.map((p) => (
              <button
                key={p.user_id}
                onClick={() => navigate(`/patients/${p.user_id}`)}
                className="w-full text-left flex items-center justify-between px-6 py-4 hover:bg-muted transition-colors"
                data-testid={`patient-row-${p.user_id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center font-semibold">
                    {p.name?.[0]?.toUpperCase() || "P"}
                  </div>
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-2">
                      {p.name}
                      {p.patient_number && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground font-mono">{p.patient_number}</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{p.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="hidden sm:block text-right">
                    <div className="text-xs text-muted-foreground">Last activity</div>
                    <div className="text-sm font-medium">{p.last_activity || "—"}</div>
                  </div>
                  <ChevronRight size={16} className="text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
